import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SingleResponseModel } from '../../modules_v2/common/models/single-response.model';
import { TaxFieldOriginModel } from './tax-field-origin/tax-field-origin.model';
import {
  Model182,
  Model182Declarado,
  Model182FieldOriginQuery,
  Model182Validation,
  normalizarDeclarado182,
  normalizarModelo182
} from './model-182/taxes-182.model';

@Injectable({
  providedIn: 'root',
})
export class Model182Service {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${environment.apiUrl}/api/v2/tax-forms/182`;
  }

  public async calculate(year: number | string, receiptNumber?: string, kind?: string): Promise<Model182> {
    let params = new HttpParams().set('year', year.toString());

    if (receiptNumber) {
      params = params.set('receiptNumber', receiptNumber);

      if (kind) {
        params = params.set('kind', kind);
      }
    }

    const response = await firstValueFrom(
      this.http.get<SingleResponseModel<Model182>>(`${this.base}/calculate`, { params }));

    return normalizarModelo182(response.value);
  }

  public async recalculate(data: Model182): Promise<Model182> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model182>>(`${this.base}/recalculate`, data));

    return normalizarModelo182(response.value);
  }

  public async validate(data: Model182): Promise<Model182Validation[]> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model182Validation[]>>(`${this.base}/validate`, data));

    return response.value || [];
  }

  public async getFile(data: Model182): Promise<{ blob: Blob, nombre: string | null }> {
    const response = await firstValueFrom(
      this.http.post(`${this.base}/file`, data, { responseType: 'blob', observe: 'response' }));

    const cabecera = response.headers.get('Content-Disposition') || '';
    const coincidencia = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecera);
    const nombre = coincidencia ? decodeURIComponent(coincidencia[1].trim()) : null;

    return { blob: new Blob([response.body], { type: 'text/plain;charset=iso-8859-1' }), nombre };
  }

  public async importFile(contenido: string): Promise<Model182> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model182>>(`${this.base}/import`, { contenido }));

    return normalizarModelo182(response.value);
  }

  public async saveRecipient(taxYear: number, declarado: Model182Declarado): Promise<Model182Declarado> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model182Declarado>>(`${this.base}/recipients`, { taxYear, declarado }));

    return normalizarDeclarado182(response.value);
  }

  public async deleteRecipient(uid: string): Promise<void> {
    await firstValueFrom(this.http.delete<SingleResponseModel<boolean>>(`${this.base}/recipients/${uid}`));
  }

  public async getOrigen(taxYear: number, declarado: Model182Declarado): Promise<TaxFieldOriginModel> {
    const query: Model182FieldOriginQuery = {
      campo: 'importe',
      taxYear,
      nif: declarado.nif,
      contactUid: null,
      valorEnElFormulario: declarado.importe
    };

    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<TaxFieldOriginModel>>(`${this.base}/field-origin`, query));

    return response.value;
  }
}
